/**
 * ItemList implementation of ng-select
 * Licensed under the MIT License
 * https://github.com/ng-select/ng-select/blob/master/LICENSE
 *
 * Modified for ux.ui-community
 */

import { isDefined, isFunction, isObject } from "../utils";
import { Host, Option, SelectionModel } from "./types";

type OptionGroups = Map<string | Option, Option[]>;

export class ItemsList {
    private _groups: OptionGroups;

    constructor(private _host: Host, private _selectionModel: SelectionModel) {}

    private _items: Option[] = [];

    get items(): Option[] {
        return this._items;
    }

    private _filteredItems: Option[] = [];

    get filteredItems(): Option[] {
        return this._filteredItems;
    }

    private _markedIndex = -1;

    get markedIndex(): number {
        return this._markedIndex;
    }

    get selectedItems() {
        return this._selectionModel.value;
    }

    get markedItem(): Option {
        return this._filteredItems[this._markedIndex];
    }

    get noItemsToSelect(): boolean {
        return (
            this._host.hideSelected &&
            this._items.length === this.selectedItems.length
        );
    }

    get maxItemsSelected(): boolean {
        return (
            this._host.multiple &&
            this._host.maxSelectedItems <= this.selectedItems.length
        );
    }

    get lastSelectedItem() {
        let i = this.selectedItems.length - 1;
        for (; i >= 0; i--) {
            let item = this.selectedItems[i];
            if (!item.disabled) {
                return item;
            }
        }
        return null;
    }

    get isSelectAll() {
        if (this._host.selectableGroupAsModel) {
            return this.items.every((option) => option.selected);
        } else {
            return this.selectedItems.length === this.items.length;
        }
    }

    get totalSelectedItemCount() {
        return this._items.reduce(
            (prev, curr) => (!curr.children && curr.selected ? prev + 1 : prev),
            0
        );
    }

    setItems(items: any[]) {
        this._items = items.map((item, index) => this.mapItem(item, index));
        if (this._host.groupBy) {
            this._groups = this._groupBy(this._items, this._host.groupBy);
            this._items = this._flatten(this._groups);
        } else {
            this._groups = new Map();
            this._groups.set(undefined, this._items);
        }
        this._filteredItems = [...this._items];
    }

    select(item: Option) {
        if (item.selected || this.maxItemsSelected) {
            return;
        }
        const multiple = this._host.multiple;
        if (!multiple) {
            this.clearSelected();
        }

        this._selectionModel.select(
            item,
            multiple,
            this._host.selectableGroupAsModel
        );
        if (this._host.hideSelected) {
            this._hideSelected(item);
        }
    }

    unselect(item: Option) {
        if (!item.selected) {
            return;
        }
        this._selectionModel.unselect(item, this._host.multiple);
        if (
            this._host.hideSelected &&
            isDefined(item.index) &&
            this._host.multiple
        ) {
            this._showSelected(item);
        }
    }

    findItem(value: any): Option {
        let findBy: (item: Option) => boolean;
        if (this._host.compareWithFn) {
            findBy = (item) => this._host.compareWithFn(item.value, value);
        } else if (this._host.bindValue) {
            findBy = (item) => {
                if (item.children) {
                    return item.value === value;
                }
                return (
                    this.resolveNested(item.value, this._host.bindValue) ===
                    value
                );
            };
        } else {
            findBy = (item) =>
                item.value === value ||
                (!item.children &&
                    item.label &&
                    item.label ===
                        this.resolveNested(value, this._host.bindLabel));
        }
        return this._items.find((item) => findBy(item));
    }

    addItem(item: any) {
        const option = this.mapItem(item, this._items.length);
        this._items.push(option);
        this._filteredItems.push(option);
        return option;
    }

    clearSelected(keepDisabled = false) {
        this._selectionModel.clear(keepDisabled);
        this._items.forEach((item) => {
            item.selected = keepDisabled && item.selected && item.disabled;
            if (isDefined(item.mixed)) {
                item.mixed = false;
            }
        });
        if (this._host.hideSelected) {
            this.resetFilteredItems();
        }
    }

    findByLabel(term: string) {
        term = term.toLocaleLowerCase();
        return this.filteredItems.find((item) => {
            const label = item.label.toLocaleLowerCase();
            return label.substr(0, term.length) === term;
        });
    }

    filter(term: string): void {
        if (!term) {
            this.resetFilteredItems();
            return;
        }

        this._filteredItems = [];
        term = this._host.searchFn ? term : term.toLocaleLowerCase();
        const match = this._host.searchFn || this._defaultSearchFn;
        const hideSelected = this._host.hideSelected;

        for (const key of Array.from(this._groups.keys())) {
            const matchedItems = [];
            for (const item of this._groups.get(key)) {
                if (
                    hideSelected &&
                    ((item.parent && item.parent.selected) || item.selected)
                ) {
                    continue;
                }
                const searchItem = this._host.searchFn ? item.value : item;
                if (match(term, searchItem)) {
                    matchedItems.push(item);
                }
            }
            if (matchedItems.length > 0) {
                const [last] = matchedItems.slice(-1);
                if (last.parent) {
                    const head = this._items.find((x) => x === last.parent);
                    this._filteredItems.push(head);
                }
                this._filteredItems.push(...matchedItems);
            }
        }
    }

    resetFilteredItems() {
        if (this._filteredItems.length === this._items.length) {
            return;
        }

        if (this._host.hideSelected && this.selectedItems.length > 0) {
            this._filteredItems = this._items.filter((x) => !x.selected);
        } else {
            this._filteredItems = this._items;
        }
    }

    unmarkItem() {
        this._markedIndex = -1;
    }

    markNextItem() {
        this._stepToItem(+1);
    }

    markPreviousItem() {
        this._stepToItem(-1);
    }

    markItem(item: Option) {
        this._markedIndex = this._filteredItems.indexOf(item);
    }

    markSelectedOrDefault(markDefault?: boolean) {
        if (this._filteredItems.length === 0) {
            return;
        }

        const lastMarkedIndex = this._getLastMarkedIndex();
        if (lastMarkedIndex > -1) {
            this._markedIndex = lastMarkedIndex;
        } else {
            this._markedIndex = markDefault
                ? this.filteredItems.findIndex((x) => !x.disabled)
                : -1;
        }
    }

    resolveNested(option: any, key: string): any {
        if (!isObject(option)) {
            return option;
        }
        if (key.indexOf(".") === -1) {
            return option[key];
        } else {
            let keys: string[] = key.split(".");
            let value = option;
            for (let i = 0, len = keys.length; i < len; ++i) {
                if (value == null) {
                    return null;
                }
                value = value[keys[i]];
            }
            return value;
        }
    }

    mapItem(item: any, index: number): Option {
        const label = this.resolveNested(item, this._host.bindLabel);
        return {
            index: index,
            label: isDefined(label) ? label.toString() : "",
            value: item,
            disabled: item.disabled,
            icon: item.icon,
        };
    }

    mapSelectedItems() {
        const multiple = this._host.multiple;
        for (const selected of this.selectedItems) {
            const value = this._host.bindValue
                ? this.resolveNested(selected.value, this._host.bindValue)
                : selected.value;
            const item = isDefined(value) ? this.findItem(value) : null;
            this._selectionModel.unselect(selected, multiple);
            this._selectionModel.select(
                item || selected,
                multiple,
                this._host.selectableGroupAsModel
            );
        }

        if (this._host.hideSelected) {
            this._filteredItems = this.filteredItems.filter(
                (x) => this.selectedItems.indexOf(x) === -1
            );
        }
    }

    private _showSelected(item: Option) {
        this._filteredItems.push(item);
        if (item.parent) {
            const parent = item.parent;
            const parentExists = this._filteredItems.find((x) => x === parent);
            if (!parentExists) {
                this._filteredItems.push(parent);
            }
        } else if (item.children) {
            for (const child of item.children) {
                child.selected = false;
                this._filteredItems.push(child);
            }
        }
        this._filteredItems = [
            ...this._filteredItems.sort((a, b) => a.index - b.index),
        ];
    }

    private _hideSelected(item: Option) {
        this._filteredItems = this._filteredItems.filter((x) => x !== item);
        if (item.parent) {
            const children = item.parent.children;
            if (children.every((x) => x.selected)) {
                this._filteredItems = this._filteredItems.filter(
                    (x) => x !== item.parent
                );
            }
        } else if (item.children) {
            this._filteredItems = this.filteredItems.filter(
                (x) => x.parent !== item
            );
        }
    }

    private _defaultSearchFn(search: string, opt: Option) {
        const label = opt.label.toLocaleLowerCase();
        return label.indexOf(search) > -1;
    }

    private _getNextItemIndex(steps: number) {
        if (steps > 0) {
            return this._markedIndex >= this._filteredItems.length - 1
                ? 0
                : this._markedIndex + 1;
        }
        return this._markedIndex <= 0
            ? this._filteredItems.length - 1
            : this._markedIndex - 1;
    }

    private _stepToItem(steps: number) {
        if (
            this._filteredItems.length === 0 ||
            this._filteredItems.every((x) => x.disabled)
        ) {
            return;
        }

        this._markedIndex = this._getNextItemIndex(steps);
        if (this.markedItem.disabled) {
            this._stepToItem(steps);
        }
    }

    private _getLastMarkedIndex() {
        if (this._host.hideSelected) {
            return -1;
        }

        if (this._markedIndex > -1 && this.markedItem === undefined) {
            return -1;
        }

        const selectedIndex = this._filteredItems.indexOf(
            this.lastSelectedItem
        );
        if (this.lastSelectedItem && selectedIndex < 0) {
            return -1;
        }

        return Math.max(this.markedIndex, selectedIndex);
    }

    private _groupBy(items: Option[], prop: string | Function): OptionGroups {
        const groups = new Map<string | Option, Option[]>();
        if (items.length === 0) {
            return groups;
        }

        // Check if items are already grouped by given key.
        if (Array.isArray(items[0].value[<string>prop])) {
            for (const item of items) {
                const children = (
                    item.value[<string>prop] || []
                ).map((x, index) => this.mapItem(x, index));
                groups.set(item, children);
            }
            return groups;
        }

        const isFnKey = isFunction(this._host.groupBy);
        const keyFn = (item: Option) => {
            let key = isFnKey
                ? (<Function>prop)(item.value)
                : item.value[<string>prop];
            return isDefined(key) ? key : undefined;
        };

        // Group items by key.
        for (const item of items) {
            let key = keyFn(item);
            const group = groups.get(key);
            if (group) {
                group.push(item);
            } else {
                groups.set(key, [item]);
            }
        }
        return groups;
    }

    private _flatten(groups: OptionGroups) {
        const isGroupByFn = isFunction(this._host.groupBy);
        const items = [];
        for (const key of Array.from(groups.keys())) {
            let i = items.length;
            if (key === undefined) {
                const withoutGroup = groups.get(undefined) || [];
                items.push(
                    ...withoutGroup.map((x) => {
                        x.index = i++;
                        return x;
                    })
                );
                continue;
            }

            const isObjectKey = isObject(key);
            const parent: Option = {
                label: isObjectKey ? (<any>key).label : String(key),
                children: undefined,
                parent: null,
                index: i++,
                disabled: !this._host.selectableGroup,
            };
            const groupKey = isGroupByFn
                ? this._host.bindLabel
                : <string>this._host.groupBy;
            const groupValue =
                this._host.groupValueFn ||
                (() => {
                    if (isObjectKey) {
                        return (<Option>key).value;
                    }
                    return { [groupKey]: key };
                });
            const children = groups.get(key).map((x) => {
                x.parent = parent;
                x.children = undefined;
                x.index = i++;
                return x;
            });
            parent.children = children;
            parent.value = groupValue(
                key,
                children.map((x) => x.value)
            );
            items.push(parent);
            items.push(...children);
        }
        return items;
    }
}
